import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import NotificationBell from '../components/NotificationBell';
import OnboardingModal from '../components/OnboardingModal';
import RemoteSupportButton from '../components/RemoteSupportButton';
import { 
  LayoutDashboard, 
  Ticket, 
  FileText, 
  FileCheck, 
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Globe,
  MessageSquare,
  GitBranch,
  Users,
  MapPin,
  ClipboardList,
  FolderOpen,
  HelpCircle,
  Network,
  BookMarked,
  Package,
  Bell,
  Mail,
  Scale,
  DollarSign,
  BookOpen,
  Clock,
  Receipt,
  Headphones,
  CreditCard,
  ServerCog,
  Server,
  FileStack,
  Wrench,
  ClipboardCheck
} from 'lucide-react';

const LOGO_ICON = "/whitewick-logo.png";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = () => {
  const { user, logout, refreshUser } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Check if onboarding is needed on mount
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user || onboardingChecked) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/onboarding/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.onboarding_required) {
            setShowOnboarding(true);
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      } finally {
        setOnboardingChecked(true);
      }
    };
    
    checkOnboarding();
  }, [user, onboardingChecked]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (refreshUser) refreshUser();
  };

  // Get user permissions with defaults
  const resolvedPerms = user?.resolved_permissions || {};
  const canView = (mod) => resolvedPerms[mod]?.view !== false;
  const isManager = user?.is_manager === true;

  // Collapsible section state
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('portal_nav_sections') || '{}');
    } catch { return {}; }
  });

  const toggleSection = (key) => {
    setExpandedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('portal_nav_sections', JSON.stringify(next));
      return next;
    });
  };

  // Auto-expand section containing active route
  useEffect(() => {
    for (const group of navGroups) {
      if (group.items?.some(i => isActive(i.path))) {
        setExpandedSections(prev => ({ ...prev, [group.key]: true }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Build grouped navigation
  const navGroups = [
    {
      key: 'main',
      standalone: { path: '/dashboard', icon: LayoutDashboard, labelKey: 'sidebar_dashboard', show: true },
    },
    {
      key: 'support',
      label: 'Support',
      icon: Headphones,
      items: [
        { path: '/dashboard/tickets', icon: Ticket, labelKey: 'sidebar_tickets', show: canView('tickets') },
        { path: '/dashboard/interventions', icon: MapPin, labelKey: 'sidebar_interventions', show: canView('interventions') },
        { path: '/dashboard/changes', icon: GitBranch, labelKey: 'sidebar_changes', show: canView('changes') },
      ].filter(i => i.show),
    },
    {
      key: 'billing',
      label: 'Billing',
      icon: CreditCard,
      items: [
        { path: '/dashboard/invoices', icon: FileText, labelKey: 'sidebar_invoices', show: canView('invoices') },
        { path: '/dashboard/proposals', icon: FileCheck, labelKey: 'sidebar_proposals', show: canView('proposals') },
        { path: '/dashboard/quotes', icon: DollarSign, labelKey: 'sidebar_quotes', show: canView('quotes') },
        { path: '/dashboard/contracts', icon: Scale, labelKey: 'sidebar_contracts', show: canView('contracts') },
        { path: '/dashboard/expenses', icon: Receipt, labelKey: 'sidebar_expenses', show: canView('expenses') },
        { path: '/dashboard/hours', icon: Clock, labelKey: 'sidebar_hours', show: canView('hours') },
      ].filter(i => i.show),
    },
    {
      key: 'resources',
      label: 'Resources',
      icon: ServerCog,
      items: [
        { path: '/dashboard/network', icon: Network, labelKey: 'sidebar_network', show: canView('network') },
        { path: '/dashboard/racks', icon: Server, labelKey: 'sidebar_racks', show: true },
        { path: '/dashboard/maintenance-notices', icon: Wrench, labelKey: 'sidebar_maintenance', show: true },
        { path: '/dashboard/assessments', icon: ClipboardCheck, labelKey: 'sidebar_assessments', show: true },
        { path: '/dashboard/ipam', icon: Network, labelKey: 'sidebar_ipam', show: canView('ipam') },
        { path: '/dashboard/assets', icon: Package, labelKey: 'sidebar_assets', show: canView('assets') },
        { path: '/dashboard/service-catalog', icon: BookOpen, labelKey: 'sidebar_service_catalog', show: canView('service_catalog') },
        { path: '/dashboard/knowledge-base', icon: BookMarked, labelKey: 'sidebar_kb', show: canView('knowledge_base') },
        { path: '/dashboard/faq', icon: HelpCircle, labelKey: 'sidebar_faq', show: canView('faq') },
      ].filter(i => i.show),
    },
    {
      key: 'documents',
      label: 'Documents',
      icon: FileStack,
      items: [
        { path: '/dashboard/files', icon: FolderOpen, labelKey: 'sidebar_files', show: canView('files') },
        { path: '/dashboard/onboarding', icon: ClipboardList, labelKey: 'sidebar_onboarding', show: canView('onboarding') },
      ].filter(i => i.show),
    },
    {
      key: 'communication',
      label: 'Communication',
      icon: MessageSquare,
      items: [
        { path: '/dashboard/messages', icon: MessageSquare, labelKey: 'sidebar_messages', show: canView('messages') },
        { path: '/dashboard/emails', icon: Mail, labelKey: 'sidebar_emails', show: canView('emails') },
        { path: '/dashboard/notifications', icon: Bell, labelKey: 'sidebar_notifications', show: canView('notifications') },
      ].filter(i => i.show),
    },
    {
      key: 'account',
      label: 'Account',
      icon: User,
      items: [
        { path: '/dashboard/team', icon: Users, labelKey: 'sidebar_team', show: isManager && canView('team') },
        { path: '/dashboard/account', icon: User, labelKey: 'sidebar_account', show: true },
      ].filter(i => i.show),
    },
  ].filter(g => g.standalone?.show || (g.items && g.items.length > 0));

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const getAllNavItems = () => {
    const items = [];
    for (const g of navGroups) {
      if (g.standalone) items.push(g.standalone);
      if (g.items) items.push(...g.items);
    }
    return items;
  };

  const getCurrentPageTitle = () => {
    const item = getAllNavItems().find(item => isActive(item.path));
    return item ? t(item.labelKey) : t('sidebar_dashboard');
  };

  const renderNavLink = (item, onClick) => (
    <Link
      key={item.path}
      to={item.path}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
        isActive(item.path)
          ? 'bg-blue-50 text-[#1e3a5f] border border-blue-100 font-semibold'
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
      }`}
      data-testid={`nav-${item.labelKey.replace('sidebar_', '')}`}
    >
      <item.icon className="h-4 w-4" />
      <span>{t(item.labelKey)}</span>
    </Link>
  );

  const renderNavGroup = (group, onClick) => {
    if (group.standalone) return renderNavLink(group.standalone, onClick);
    const isExpanded = expandedSections[group.key] !== false;
    const hasActive = group.items.some(i => isActive(i.path));
    return (
      <div key={group.key} data-testid={`nav-group-${group.key}`}>
        <button
          onClick={() => toggleSection(group.key)}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm transition-colors ${
            hasActive ? 'text-[#1e3a5f] font-semibold' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-3">
            <group.icon className="h-4 w-4" />
            <span>{group.label}</span>
          </div>
          {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {isExpanded && (
          <div className="ml-4 border-l border-slate-100 pl-2 space-y-0.5 mt-0.5">
            {group.items.map(item => renderNavLink(item, onClick))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Onboarding Modal */}
      <OnboardingModal 
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={handleOnboardingComplete}
      />
      
      <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-72 fixed inset-y-0 left-0 z-30 bg-white border-r border-slate-200">
        {/* Logo */}
        <div className="h-28 flex items-center px-4 border-b border-slate-200">
          <img src={LOGO_ICON} alt="Whitewick IT" className="h-24 w-auto" />
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
          {navGroups.map(group => renderNavGroup(group))}
        </nav>
        
        {/* Remote Support Button */}
        <div className="px-4 pb-4">
          <RemoteSupportButton variant="sidebar" language={language} context="client" />
        </div>
        
        {/* User Section */}
        <div className="p-4 border-t border-slate-200">
          <div className="p-3 rounded-lg bg-slate-50">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.company_name}</div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className="h-24 flex items-center justify-between px-4 border-b border-slate-200">
          <img src={LOGO_ICON} alt="Whitewick IT" className="h-20 w-auto" />
          <button onClick={() => setSidebarOpen(false)} className="text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
          {navGroups.map(group => renderNavGroup(group, () => setSidebarOpen(false)))}
        </nav>
        
        {/* User Section */}
        <div className="p-4 border-t border-slate-200">
          <div className="p-3 rounded-lg bg-slate-50">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.company_name}</div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-72 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b border-slate-200 bg-white sticky top-0 z-20">
          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-slate-500 hover:text-slate-900"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-sidebar-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          
          {/* Page Title - Hidden on mobile */}
          <div className="hidden lg:block text-lg font-semibold text-slate-900 font-['Sora']">
            {getCurrentPageTitle()}
          </div>
          
          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition-colors text-sm"
              data-testid="dashboard-language-toggle"
            >
              <Globe className="h-4 w-4" />
              {language === 'en' ? 'FR' : 'EN'}
            </button>
            
            {/* Notifications */}
            <NotificationBell />
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-slate-700" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white font-medium">
                    {user?.full_name?.charAt(0) || 'U'}
                  </div>
                  <span className="hidden sm:inline">{user?.full_name}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200">
                <div className="px-3 py-2">
                  <div className="text-sm font-medium text-slate-900">{user?.full_name}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </div>
                <DropdownMenuSeparator className="bg-slate-200" />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/account" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    {t('dashboard_account_settings')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-200" />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('sidebar_signout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        
        {/* Main Content Area */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
    </>
  );
};

export default DashboardLayout;
